// First-touch acquisition capture. Reads UTM params + referrer on the
// first page load, normalizes them into a single channel name, and keeps
// the value in localStorage so it survives until the visitor signs up and
// authenticates (the backend persists it first-touch). Stored once; later
// visits never overwrite the original channel.

const KEY = "maren_attribution";
const SENT = "maren_attribution_sent";

export interface Attribution {
  source: string;
  medium: string | null;
  campaign: string | null;
  referrer: string | null;
  landing: string | null;
}

// Identity providers are a step INSIDE our own signup flow, never an
// acquisition channel. Before this list existed, a visitor whose stored
// attribution was missing at the moment they returned from Google OAuth
// was recorded as source=google, which is indistinguishable in the
// marketing table from a real Google search visit. Google is genuinely
// the second largest real channel here (31 signups referred from
// google.com/ landing on /), so letting OAuth noise pile into that same
// bucket corrupts the one organic number worth trusting.
const AUTH_PROVIDER_HOSTS = [
  "accounts.google.com",
  "appleid.apple.com",
  "login.microsoftonline.com",
  "github.com/login",
  "auth0.com",
  "supabase.co",
  "supabase.in",
];

function isAuthProviderReferrer(ref: string): boolean {
  const r = ref.toLowerCase();
  return AUTH_PROVIDER_HOSTS.some((h) => r.includes(h));
}

/**
 * Social apps open links in an embedded webview that sends NO referrer,
 * so this traffic lands as "direct" and becomes unattributable. That is
 * where the TikTok audience actually was: 35 of 37 "direct" signups
 * landed on "/" with an empty referrer while a pinned TikTok post was
 * driving views, and the tiktok bucket sat at zero the whole time.
 *
 * The User-Agent is the only signal left in that case. It is an
 * inference rather than a referrer, so the result is recorded with a
 * referrer of "app://<source>" to keep it auditable and to avoid
 * claiming an HTTP referrer that never existed. A UTM link still wins
 * over this, and should still be used wherever a link can be edited.
 */
const IN_APP_BROWSERS: Array<{ test: RegExp; source: string }> = [
  // TikTok ships several webview identifiers across regions and versions.
  { test: /bytedancewebview|musical_ly|tiktok|trill_|bytelocale/i, source: "tiktok" },
  { test: /instagram/i, source: "instagram" },
  { test: /FBAN\/|FBAV\/|FB_IAB|FB4A/i, source: "facebook" },
  { test: /snapchat/i, source: "snapchat" },
  { test: /linkedinapp/i, source: "linkedin" },
  { test: /pinterest/i, source: "pinterest" },
  { test: /twitter|TwitterAndroid/i, source: "twitter" },
];

export function sourceFromUserAgent(ua: string): string | null {
  if (!ua) return null;
  for (const entry of IN_APP_BROWSERS) {
    if (entry.test.test(ua)) return entry.source;
  }
  return null;
}

// Map a referrer hostname to a readable channel name so the marketing
// table reads "reddit" / "google" rather than raw hosts. Unknown hosts
// fall through as the bare domain.
function channelFromHost(host: string): string {
  const h = host.toLowerCase().replace(/^www\./, "");
  if (h.includes("reddit")) return "reddit";
  if (h.includes("google")) return "google";
  if (h.includes("bing")) return "bing";
  if (h.includes("duckduckgo")) return "duckduckgo";
  if (h === "t.co" || h.includes("twitter") || h === "x.com") return "twitter";
  if (h.includes("facebook") || h === "fb.com" || h === "lm.facebook.com") return "facebook";
  if (h.includes("instagram") || h === "l.instagram.com") return "instagram";
  if (h.includes("youtube") || h === "youtu.be") return "youtube";
  if (h.includes("linkedin") || h === "lnkd.in") return "linkedin";
  if (h.includes("tiktok")) return "tiktok";
  if (h.includes("discord")) return "discord";
  if (h.includes("studentdoctor") || h === "sdn.org") return "sdn";
  return h;
}

/** Params used to carry attribution across the OAuth round trip. */
const CARRY = { source: "at_src", medium: "at_med", campaign: "at_cmp" } as const;

export function computeFirstTouch(
  href: string = window.location.href,
  referrer: string = document.referrer || "",
  userAgent: string = typeof navigator === "undefined" ? "" : navigator.userAgent,
): Attribution | null {
  try {
    const url = new URL(href);
    const p = url.searchParams;
    const utmSource = (p.get("utm_source") || "").trim().toLowerCase();
    const utmMedium = (p.get("utm_medium") || "").trim().toLowerCase() || null;
    const utmCampaign = (p.get("utm_campaign") || "").trim() || null;
    const landing = (url.pathname + url.search).slice(0, 500);

    // Attribution we handed to the OAuth provider on the way out. The
    // round trip can land in a different storage context than the one
    // that captured the original touch (a social in-app browser handing
    // sign-in to the system browser is the common case), so without this
    // the true source is simply gone by the time the account exists.
    const carried = (p.get(CARRY.source) || "").trim().toLowerCase();
    if (carried) {
      return {
        source: carried,
        medium: (p.get(CARRY.medium) || "").trim().toLowerCase() || null,
        campaign: (p.get(CARRY.campaign) || "").trim() || null,
        referrer: "oauth://restored",
        landing,
      };
    }

    const ref = referrer;
    let refHost = "";
    try {
      refHost = ref ? new URL(ref).hostname : "";
    } catch {
      refHost = "";
    }
    const ourHost = url.hostname.replace(/^www\./, "");
    const externalRef =
      Boolean(refHost) &&
      refHost.replace(/^www\./, "") !== ourHost &&
      // An identity provider is part of our own signup, not a channel.
      !isAuthProviderReferrer(ref);

    let source: string;
    let medium = utmMedium;
    let recordedReferrer: string | null = externalRef ? ref.slice(0, 500) : null;

    if (utmSource) {
      source = utmSource;
    } else if (externalRef) {
      source = channelFromHost(refHost);
      if (!medium) medium = "referral";
    } else {
      // No referrer at all. Before calling it direct, check whether this
      // is a social app's embedded browser, which strips the referrer.
      const inApp = sourceFromUserAgent(userAgent);
      if (inApp) {
        source = inApp;
        if (!medium) medium = "social";
        // Not an HTTP referrer. Marked so the inference stays auditable.
        recordedReferrer = `app://${inApp}`;
      } else {
        source = "direct";
      }
    }

    return {
      source,
      medium,
      campaign: utmCampaign,
      referrer: recordedReferrer,
      landing,
    };
  } catch {
    return null;
  }
}

/**
 * Query string carrying the stored first touch through an OAuth redirect.
 * Empty when there is nothing worth carrying, so the callback URL stays
 * clean for the ordinary direct visitor.
 */
export function attributionCarryParams(): URLSearchParams {
  const params = new URLSearchParams();
  const a = getAttribution();
  if (!a || !a.source || a.source === "direct") return params;
  params.set(CARRY.source, a.source);
  if (a.medium) params.set(CARRY.medium, a.medium);
  if (a.campaign) params.set(CARRY.campaign, a.campaign);
  return params;
}

// No-op once a value is stored, so only the first landing of a session
// sticks (first-touch attribution).
export function captureFirstTouch(): void {
  try {
    if (localStorage.getItem(KEY)) return;
    const a = computeFirstTouch();
    if (a) localStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    // localStorage can throw in privacy modes; attribution is best-effort.
  }
}

export function getAttribution(): Attribution | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

export function attributionAlreadySent(): boolean {
  try {
    return localStorage.getItem(SENT) === "1";
  } catch {
    return false;
  }
}

export function markAttributionSent(): void {
  try {
    localStorage.setItem(SENT, "1");
  } catch {
    // ignore
  }
}
